import { useState, useEffect } from 'react';
import { paymentConfigAPI, type PaymentConfig } from '../../services/api';

const ALL_PAY_TYPES = [
  { key: 'alipay', label: '支付宝', color: 'blue' },
  { key: 'wxpay', label: '微信支付', color: 'green' },
  { key: 'qqpay', label: 'QQ钱包', color: 'indigo' },
];

const PAY_TYPE_ACTIVE_CLASS: Record<string, string> = {
  blue: 'border-blue-500 bg-blue-100 text-blue-700',
  green: 'border-green-500 bg-green-100 text-green-700',
  indigo: 'border-indigo-500 bg-indigo-100 text-indigo-700',
};

export default function PaymentConfigTab() {
  const [config, setConfig] = useState<Partial<PaymentConfig>>({
    isEnabled: false,
    apiUrl: '',
    merchantId: '',
    platformPublicKey: '',
    merchantPrivateKey: '',
    merchantMd5Key: '',
    signType: 'RSA',
    notifyUrl: '',
    returnUrl: '',
    creditExchangeRate: 10,
    enabledPayTypes: ['alipay', 'wxpay', 'qqpay'],
    minRechargeAmount: 0.01,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const signType = (config.signType || 'RSA').toUpperCase();
  const isMd5Mode = signType === 'MD5';

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const data = await paymentConfigAPI.getConfig();
      setConfig(data);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || '加载配置失败' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await paymentConfigAPI.updateConfig(config);
      setMessage({ type: 'success', text: '配置保存成功' });
      loadConfig();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || '保存失败' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setMessage(null);
    try {
      await paymentConfigAPI.testConfig(config);
      setMessage({ type: 'success', text: '配置测试成功，签名验证通过' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || '测试失败' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold text-gray-900">支付配置</h2>
        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            onClick={handleTest}
            disabled={testing}
            className="w-full rounded-lg bg-gray-100 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-50 sm:w-auto"
          >
            {testing ? '测试中...' : '测试配置'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:opacity-50 sm:w-auto"
          >
            {saving ? '保存中...' : '保存配置'}
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="space-y-6 rounded-lg bg-white p-4 shadow sm:p-6">
        {/* 启用开关 */}
        <div className="flex flex-col gap-4 rounded-lg bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-medium text-gray-900">启用支付功能</h3>
            <p className="text-sm text-gray-500">开启后用户可以通过支付充值积分</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.isEnabled}
              onChange={(e) => setConfig({ ...config, isEnabled: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        {/* 签名模式 */}
        <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            支付签名模式
          </label>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className={`flex items-start gap-3 rounded-lg border-2 p-4 transition-all ${
              !isMd5Mode ? 'border-blue-500 bg-white' : 'border-gray-200 bg-white hover:border-gray-300'
            }`}>
              <input
                type="radio"
                name="signType"
                value="RSA"
                checked={!isMd5Mode}
                onChange={() => setConfig({ ...config, signType: 'RSA' })}
                className="mt-1"
              />
              <span>
                <span className="block font-medium text-gray-900">RSA 支付模式</span>
                <span className="block text-sm text-gray-500 mt-1">使用平台公钥和商户私钥，提交到 /api/pay/submit</span>
              </span>
            </label>
            <label className={`flex items-start gap-3 rounded-lg border-2 p-4 transition-all ${
              isMd5Mode ? 'border-blue-500 bg-white' : 'border-gray-200 bg-white hover:border-gray-300'
            }`}>
              <input
                type="radio"
                name="signType"
                value="MD5"
                checked={isMd5Mode}
                onChange={() => setConfig({ ...config, signType: 'MD5' })}
                className="mt-1"
              />
              <span>
                <span className="block font-medium text-gray-900">MD5 商户密钥模式</span>
                <span className="block text-sm text-gray-500 mt-1">使用商户 MD5 密钥，提交到 /submit.php</span>
              </span>
            </label>
          </div>
        </div>

        {/* 基本配置 */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              支付接口地址
            </label>
            <input
              type="text"
              value={config.apiUrl || ''}
              onChange={(e) => setConfig({ ...config, apiUrl: e.target.value })}
              placeholder="https://pay.example.com/"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">彩虹易支付接口地址，以 / 结尾</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              商户ID
            </label>
            <input
              type="text"
              value={config.merchantId || ''}
              onChange={(e) => setConfig({ ...config, merchantId: e.target.value })}
              placeholder="1000"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* 密钥配置 */}
        {isMd5Mode ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                商户MD5密钥
              </label>
              <input
                type="text"
                value={config.merchantMd5Key || ''}
                onChange={(e) => setConfig({ ...config, merchantMd5Key: e.target.value })}
                placeholder="请输入商户 MD5 密钥"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">MD5 模式只需要商户ID和商户MD5密钥，不使用平台公钥和商户私钥</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                平台公钥（用于验签）
              </label>
              <textarea
                value={config.platformPublicKey || ''}
                onChange={(e) => setConfig({ ...config, platformPublicKey: e.target.value })}
                placeholder="-----BEGIN PUBLIC KEY-----..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                商户私钥（用于RSA签名）
              </label>
              <textarea
                value={config.merchantPrivateKey || ''}
                onChange={(e) => setConfig({ ...config, merchantPrivateKey: e.target.value })}
                placeholder="-----BEGIN PRIVATE KEY-----..."
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">请妥善保管私钥，不要泄露给他人</p>
            </div>
          </div>
        )}

        {/* 回调配置 */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              异步通知URL（可选）
            </label>
            <input
              type="text"
              value={config.notifyUrl || ''}
              onChange={(e) => setConfig({ ...config, notifyUrl: e.target.value })}
              placeholder="https://your-domain.com/api/payment/notify"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">留空则使用系统自动生成的地址</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              同步跳转URL（可选）
            </label>
            <input
              type="text"
              value={config.returnUrl || ''}
              onChange={(e) => setConfig({ ...config, returnUrl: e.target.value })}
              placeholder="https://your-domain.com/payment/return"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">留空则使用系统自动生成的地址</p>
          </div>
        </div>


        {/* 积分兑换比例 */}
        <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            积分兑换比例（1元 = ? 积分）
          </label>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <span className="text-gray-600">1 元 =</span>
            <input
              type="number"
              min="0.1"
              step="0.1"
              value={config.creditExchangeRate || 10}
              onChange={(e) => setConfig({ ...config, creditExchangeRate: parseFloat(e.target.value) || 10 })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-center focus:border-transparent focus:ring-2 focus:ring-blue-500 sm:w-24"
            />
            <span className="text-gray-600">积分</span>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            设置自定义金额充值时的兑换比例。例如：设置 10 表示 1元 = 10积分，充值 5元 可获得 50积分
          </p>
        </div>

        {/* 支付方式配置 */}
        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            启用的支付方式
          </label>
          <div className="flex flex-wrap gap-3">
            {ALL_PAY_TYPES.map((payType) => {
              const isEnabled = config.enabledPayTypes?.includes(payType.key) ?? false;
              return (
                <label
                  key={payType.key}
                  className={`flex min-w-[120px] items-center justify-center gap-2 rounded-lg border-2 px-4 py-2 transition-all ${
                    isEnabled
                      ? PAY_TYPE_ACTIVE_CLASS[payType.color]
                      : 'border-gray-300 bg-white text-gray-500 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={(e) => {
                      const currentTypes = config.enabledPayTypes || ['alipay', 'wxpay', 'qqpay'];
                      let newTypes: string[];
                      if (e.target.checked) {
                        newTypes = [...currentTypes, payType.key];
                      } else {
                        newTypes = currentTypes.filter((t) => t !== payType.key);
                      }
                      setConfig({ ...config, enabledPayTypes: newTypes });
                    }}
                    className="sr-only"
                  />
                  <span className="text-sm font-medium">{payType.label}</span>
                  {isEnabled && (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </label>
              );
            })}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            选择要在前端显示的支付方式，至少启用一种支付方式
          </p>
        </div>

        {/* 最低充值金额 */}
        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            最低充值金额（元）
          </label>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <span className="text-gray-600">¥</span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={config.minRechargeAmount || 0.01}
              onChange={(e) => setConfig({ ...config, minRechargeAmount: parseFloat(e.target.value) || 0.01 })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 sm:w-32"
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            设置自定义金额充值时的最低金额限制。例如：设置 1 表示用户自定义充值时最少需要充值 1 元
          </p>
        </div>
      </div>

      {/* 使用说明 */}
      <div className="rounded-lg bg-blue-50 p-5 sm:p-6">
        <h3 className="font-medium text-blue-900 mb-2">配置说明</h3>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>支付接口地址：彩虹易支付的API地址，通常以 https:// 开头，以 / 结尾</li>
          <li>商户ID：在彩虹易支付平台注册的商户ID</li>
          <li>平台公钥：从彩虹易支付平台获取的公钥，用于验证回调签名</li>
          <li>商户私钥：在彩虹易支付平台生成的私钥，用于请求签名</li>
          <li>配置完成后请先点击"测试配置"按钮验证配置是否正确</li>
        </ul>
      </div>
    </div>
  );
}
